import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cn } from "@/lib/utils";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "glossyPrimary"
  | "darkPrimary"
  | "outline"
  | "link"
  | "default"
  | "ghost"
  | "gradient";
export type ButtonSize = "default" | "sm" | "md" | "lg" | "icon";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Applies disabled visual styles via CSS only — does NOT set the native disabled attribute, so click events (e.g. analytics) still fire. */
  visuallyDisabled?: boolean;
  /**
   * Stilleri kendi çocuğuna uygular, `<button>` render etmez.
   * Navigasyon amaçlı butonları `<a href>` semantiğine taşımak için:
   * `<Button asChild><Link to="/pricing">Fiyatlar</Link></Button>`
   */
  asChild?: boolean;
}

const baseStyles =
  "inline-flex items-center justify-center gap-2 whitespace-nowrap transition-all duration-200 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-60";

const variantStyles: Record<ButtonVariant, string> = {
  primary:
    "rounded-[10px] bg-primary dark:bg-indigo-500 text-primary-foreground dark:text-white focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:bg-muted disabled:text-muted-foreground",
  glossyPrimary:
    "relative overflow-hidden rounded-[10px] bg-primary text-primary-foreground hover:shadow-lg after:content-[''] after:absolute after:inset-0 after:bg-gradient-to-b after:from-white/20 after:to-transparent dark:after:from-accent/15 dark:after:to-transparent focus-visible:ring-4 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:bg-muted disabled:text-muted-foreground",
  darkPrimary:
    "relative overflow-hidden rounded-[10px] bg-foreground dark:bg-card text-background dark:text-card-foreground hover:shadow-lg after:content-[''] after:absolute after:inset-0 after:bg-gradient-to-b after:from-white/20 after:to-transparent dark:after:from-accent/20 dark:after:via-primary/10 dark:after:to-transparent focus-visible:ring-4 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:bg-gray-800 disabled:text-muted-foreground  ",
  secondary:
    "rounded-[8px] bg-card text-card-foreground hover:bg-accent focus-visible:ring-4 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:bg-muted disabled:text-muted-foreground",
  outline:
    "rounded-md border border-input bg-background  hover:border-text-primary",
  link: "text-primary underline-offset-4 hover:underline bg-transparent border-0 p-0 h-auto",

  // WE CAN REMOVE THESE LATER
  default:
    "rounded-[10px] bg-primary text-primary-foreground hover:shadow-lg focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:bg-muted disabled:text-muted-foreground",
  ghost:
    "rounded-[10px] bg-transparent focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:bg-muted disabled:text-muted-foreground",
  gradient:
    "rounded-[10px] bg-gradient-harmoni-cyan-purple hover:bg-gradient-harmoni-cyan-purple/80 text-primary-foreground hover:shadow-lg focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:bg-muted disabled:text-muted-foreground",
};

const sizeStyles: Record<ButtonSize, string> = {
  default: "h-11 px-4 text-md leading-6 font-semibold",
  sm: "h-9 px-2 text-sm leading-4 font-semibold",
  md: "h-10 px-3 py-2 text-md leading-6 font-semibold",
  lg: "h-12 px-3 py-2 text-lg leading-7 font-semibold",
  icon: "w-10 h-10",
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = "primary",
      size = "default",
      visuallyDisabled,
      disabled,
      asChild,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        // `<a>` gibi native disabled desteklemeyen elemanlara attribute geçmiyoruz.
        {...(asChild ? {} : { disabled: visuallyDisabled ? false : disabled })}
        aria-disabled={visuallyDisabled || disabled}
        className={cn(
          baseStyles,
          variantStyles[variant],
          sizeStyles[size],
          visuallyDisabled && "opacity-60 cursor-not-allowed",
          className,
        )}
        {...props}
        {...(visuallyDisabled && {
          onClick: (e: React.MouseEvent<HTMLButtonElement>) =>
            e.preventDefault(),
        })}
      />
    );
  },
);

Button.displayName = "Button";

export default Button;
